import './release-countdown.component.scss';

import type { ComponentChildren } from 'preact';
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import { useTranslation } from '../../../i18n';

interface ReleaseCountdownProps {
  releaseDate: string;
  hideAtZero?: boolean;
  message?: ComponentChildren;
}

const millisecondsUntil = (date: string) => Math.max(0, new Date(date).getTime() - Date.now());

export const ReleaseCountdownComponent = ({
  releaseDate,
  hideAtZero = false,
  message,
}: ReleaseCountdownProps) => {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => millisecondsUntil(releaseDate));
  const [upcoming] = useState(() => remaining > 0);

  useEffect(() => {
    if (!upcoming) return;
    const timer = setInterval(() => {
      const next = millisecondsUntil(releaseDate);
      setRemaining(next);
      if (next === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [releaseDate, upcoming]);

  if (!upcoming || (hideAtZero && remaining === 0)) return null;

  const units = [
    { value: Math.floor(remaining / 86_400_000), label: 'D' },
    { value: Math.floor(remaining / 3_600_000) % 24, label: 'H' },
    { value: Math.floor(remaining / 60_000) % 60, label: 'M' },
    { value: Math.floor(remaining / 1000) % 60, label: 'S' },
  ];

  return (
    <div class="single-release-countdown">
      <div class="single-release-countdown-timer">
        <span class="single-release-countdown-label">{t('music.allTracksAvailableIn')}</span>
        <span class="single-release-countdown-time">
          {units.map(({ value, label }) => (
            <span key={label}>
              {value}
              <span class="single-release-countdown-unit">{label}</span>
            </span>
          ))}
        </span>
      </div>
      {message && <p class="single-release-countdown-message">{message}</p>}
    </div>
  );
};
